import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  createDefaultRandomConstraints,
  type AiVerificationAssignmentSet,
  type AiVerificationDistribution,
  type AiVerificationRandomConstraints,
} from '@citizen-walk/shared-types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessions } from '../sessions-admin';
import { useVerificationTemplates } from './useVerificationTemplates';
import { loadAssignmentSet, saveAssignmentSetPatch } from './useAssignmentSetEditor';

/**
 * 既存配分セットの編集フォーム (Admin Wave AW4、docs/108 Critical #12,#13 / High #4,#5,#6)。
 *
 * 旧 `old2/apps/admin/src/pages/VerificationAssignmentEditorPage.tsx` を v2 に踏襲:
 *   - #13: setId からセットを load して form を prefill。確認は merge update (新規 set を増やさない)。
 *   - #12: status (下書き/公開中/クローズ) select + 公開中以外は赤警告
 *     「公開中にしないと参加者に表示されません」。
 *   - #4: startAt / endAt の datetime-local。
 *   - #5: sessionIds 複数選択 (チェックボックス、未選択=地区全体)。
 *   - #6: random_per_user の制約 (上限/自分の最低数/preferOwnPosts) + 生成済 perUserAssignments 明細表。
 *
 * v2 方針との折衷:
 *   - 配分の「再生成」は generateAssignments callable (新規 set 専用) が担うため、本フォームでは
 *     candidatePostIds / perUserAssignments の再計算はしない (= 既存の生成結果を保持しつつ
 *     メタ情報・公開状態・制約デフォルトを編集する画面)。
 *   - status / 期間 / sessionIds / displayName 等の運用フィールド編集が主目的。
 */

const DIST_OPTIONS: { value: AiVerificationDistribution; label: string }[] = [
  { value: 'free', label: '自由検証（参加者が任意の AI 解析済投稿を選ぶ）' },
  { value: 'all_same', label: '全員同一（選んだ投稿群を全員に出題）' },
  { value: 'random_per_user', label: 'ランダム配分（各人に最大 N 件を自動配分）' },
  { value: 'manual', label: '個別アサイン（管理者が個別指定）' },
];

const STATUS_OPTIONS: { value: AiVerificationAssignmentSet['status']; label: string }[] = [
  { value: 'draft', label: '下書き（参加者には表示されません）' },
  { value: 'published', label: '公開中（参加者に表示されます）' },
  { value: 'closed', label: 'クローズ（受付終了）' },
];

const STATUS_TEXT: Record<AiVerificationAssignmentSet['status'], string> = {
  draft: '下書き',
  published: '公開中',
  closed: 'クローズ',
};

/** ISO 文字列 → datetime-local 入力値 (YYYY-MM-DDTHH:mm)。 */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  // すでに ISO の先頭 16 文字が local 表現に近いが、TZ オフセットを考慮して Date 経由で整形
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export interface AssignmentSetEditExistingProps {
  districtId: string;
  setId: string;
  canManage: boolean;
  actorUid: string;
}

export function AssignmentSetEditExisting({
  districtId,
  setId,
  canManage,
  actorUid,
}: AssignmentSetEditExistingProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const templatesState = useVerificationTemplates(districtId);
  const sessionsState = useSessions(districtId);

  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'notfound' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [set, setSet] = useState<AiVerificationAssignmentSet | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    loadAssignmentSet(districtId, setId)
      .then((s) => {
        if (cancelled) return;
        if (!s) {
          setLoadState('notfound');
          return;
        }
        setSet(s);
        setLoadState('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError((e as Error).message);
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, setId]);

  const templates = templatesState.status === 'ready' ? templatesState.items : [];
  const sessions =
    sessionsState.status === 'ready' ? sessionsState.items.filter((s) => !s.archivedAt) : [];

  function update<K extends keyof AiVerificationAssignmentSet>(
    key: K,
    value: AiVerificationAssignmentSet[K],
  ) {
    setSet((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  function patchConstraints(patch: Partial<AiVerificationRandomConstraints>) {
    setSet((prev) => {
      if (!prev) return prev;
      const base = prev.randomConstraints ?? createDefaultRandomConstraints();
      return { ...prev, randomConstraints: { ...base, ...patch } };
    });
  }

  function toggleSession(sessionId: string, checked: boolean) {
    setSet((prev) => {
      if (!prev) return prev;
      const cur = prev.sessionIds ?? [];
      const next = checked ? [...cur, sessionId] : cur.filter((id) => id !== sessionId);
      return {
        ...prev,
        sessionIds: next,
        // 後方互換: 単一なら sessionId にも反映、複数 or 0 件なら null
        sessionId: next.length === 1 ? next[0]! : null,
      };
    });
  }

  async function handleSave() {
    if (!set || !canManage) return;
    setSaving(true);
    try {
      await saveAssignmentSetPatch(
        districtId,
        setId,
        {
          displayName: set.displayName,
          description: set.description,
          templateId: set.templateId,
          templateVersion: set.templateVersion,
          distribution: set.distribution,
          ...(set.randomConstraints ? { randomConstraints: set.randomConstraints } : {}),
          sessionIds: set.sessionIds ?? [],
          sessionId: set.sessionId ?? null,
          startAt: set.startAt,
          endAt: set.endAt,
          status: set.status,
        },
        actorUid,
      );
      toast('success', '配分セットを保存しました');
      navigate(`/districts/${districtId}/verification/assignmentSets`);
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  if (loadState === 'loading') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-600">配分セットを読込中…</p>
      </Card>
    );
  }
  if (loadState === 'notfound') {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-600">
          配分セットが見つかりません (id: {setId})。一覧に戻ってください。
        </p>
      </Card>
    );
  }
  if (loadState === 'error' || !set) {
    return (
      <Card padding="md" className="border-danger-600">
        <p className="text-sm text-danger-700">読込エラー: {loadError ?? 'unknown'}</p>
      </Card>
    );
  }

  const perUser = set.perUserAssignments ?? {};
  const perUserEntries = Object.entries(perUser);
  const constraints = set.randomConstraints ?? createDefaultRandomConstraints();

  return (
    <>
      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">この地区の配分セット編集権限がありません。</p>
        </Card>
      )}

      {/* 基本情報 */}
      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">基本情報</h2>
        <div className="grid grid-cols-1 gap-3">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">セット名 *</span>
            <input
              type="text"
              value={set.displayName}
              onChange={(e) => update('displayName', e.target.value)}
              disabled={!canManage || saving}
              maxLength={120}
              aria-label="セット名"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          </label>
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">使用テンプレート</span>
            <select
              value={set.templateId}
              onChange={(e) => {
                const tpl = templates.find((t) => t.id === e.target.value);
                update('templateId', e.target.value);
                if (tpl) update('templateVersion', tpl.version);
              }}
              disabled={!canManage || saving}
              aria-label="使用テンプレート"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
            >
              {templates.length === 0 && (
                <option value={set.templateId}>{set.templateId || '(テンプレートなし)'}</option>
              )}
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  [{t.status}] {t.displayName || '(無題)'} (v{t.version})
                </option>
              ))}
            </select>
          </label>
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">説明</span>
            <textarea
              value={set.description}
              onChange={(e) => update('description', e.target.value)}
              disabled={!canManage || saving}
              rows={2}
              aria-label="説明"
              className="px-3 py-2 border border-slate-300 rounded text-sm"
            />
          </label>
        </div>
      </Card>

      {/* 期間 + status (#4 + #12) */}
      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">公開設定 / 期間</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">開始日時 (startAt)</span>
            <input
              type="datetime-local"
              value={isoToLocalInput(set.startAt)}
              onChange={(e) => update('startAt', localInputToIso(e.target.value))}
              disabled={!canManage || saving}
              aria-label="開始日時"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          </label>
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">期限 (endAt)</span>
            <input
              type="datetime-local"
              value={isoToLocalInput(set.endAt)}
              onChange={(e) => update('endAt', localInputToIso(e.target.value))}
              disabled={!canManage || saving}
              aria-label="期限"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm"
            />
          </label>
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">
              状態{' '}
              <span className="text-danger-700 font-semibold">
                ★必須：「公開中」にしないと参加者に表示されません
              </span>
            </span>
            <select
              value={set.status}
              onChange={(e) =>
                update('status', e.target.value as AiVerificationAssignmentSet['status'])
              }
              disabled={!canManage || saving}
              aria-label="状態"
              className={`min-h-touch px-3 border rounded text-sm bg-white ${
                set.status === 'published' ? 'border-success-500 bg-success-50' : 'border-slate-300'
              }`}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {set.status !== 'published' && (
          <p className="mt-3 text-sm text-danger-700 font-medium" role="alert">
            ⚠ 現在「{STATUS_TEXT[set.status]}」状態です。公開中にしないと参加者に表示されません。
          </p>
        )}
      </Card>

      {/* 対象セッション (#5) */}
      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">対象セッション（複数選択可）</h2>
        {sessionsState.status === 'loading' && (
          <p className="text-sm text-slate-600">セッション読込中…</p>
        )}
        {sessionsState.status === 'error' && (
          <p className="text-sm text-danger-700">
            セッション読込エラー: {sessionsState.error.message}
          </p>
        )}
        {sessionsState.status === 'ready' && sessions.length === 0 && (
          <p className="text-sm text-slate-500">
            セッションがありません（地区全体が対象になります）。
          </p>
        )}
        {sessions.length > 0 && (
          <ul className="grid grid-cols-1 gap-1 max-h-64 overflow-auto border border-slate-200 rounded p-2 bg-white">
            {sessions.map((s) => {
              const checked = (set.sessionIds ?? []).includes(s.id);
              return (
                <li key={s.id}>
                  <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleSession(s.id, e.target.checked)}
                      disabled={!canManage || saving}
                      aria-label={`セッション ${s.name || s.id}`}
                    />
                    <span>{s.name || s.id}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        <p className="mt-2 text-[11px] text-slate-500">
          <strong>未選択（チェック 0 件）</strong>の場合、地区参加者全員 /
          地区全体投稿が対象になります。
        </p>
      </Card>

      {/* 配分パターン */}
      <Card padding="lg" className="mb-4">
        <h2 className="text-base font-bold text-slate-900 mb-3">配分パターン</h2>
        <div className="grid grid-cols-1 gap-2">
          {DIST_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="distribution"
                checked={set.distribution === o.value}
                onChange={() => update('distribution', o.value)}
                disabled={!canManage || saving}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* ランダム配分制約 (#6) */}
      {set.distribution === 'random_per_user' && (
        <Card padding="lg" className="mb-4">
          <h2 className="text-base font-bold text-slate-900 mb-3">配分制約 (random_per_user)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">1人あたりの最大割当数（上限）</span>
              <input
                type="number"
                min={1}
                max={50}
                value={constraints.minPostsPerUser}
                onChange={(e) =>
                  patchConstraints({
                    minPostsPerUser: Math.max(1, parseInt(e.target.value, 10) || 1),
                  })
                }
                disabled={!canManage || saving}
                aria-label="minPostsPerUser"
                className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
              />
            </label>
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">
                うち自分の投稿の最低数 (minOwnPosts、preferOwnPosts=OFF 時のみ有効)
              </span>
              <input
                type="number"
                min={0}
                max={50}
                value={constraints.minOwnPosts}
                onChange={(e) =>
                  patchConstraints({
                    minOwnPosts: Math.max(0, parseInt(e.target.value, 10) || 0),
                  })
                }
                disabled={!canManage || saving}
                aria-label="minOwnPosts"
                className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!constraints.preferOwnPosts}
                onChange={(e) => patchConstraints({ preferOwnPosts: e.target.checked })}
                disabled={!canManage || saving}
                aria-label="preferOwnPosts"
              />
              自分の投稿で上限まで埋める（OFF にすると最低数までに抑える）
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!constraints.preferLessVerified}
                onChange={(e) => patchConstraints({ preferLessVerified: e.target.checked })}
                disabled={!canManage || saving}
                aria-label="preferLessVerified"
              />
              票が偏らないよう既検証少を優先 (preferLessVerified)
            </label>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            ※ 制約を変更したら保存後、配分の再生成は「新規セット作成」(generateAssignments)
            から行ってください。
            本フォームは既存の生成結果を保持したまま運用フィールドを編集します。
          </p>
        </Card>
      )}

      {/* 生成済 perUserAssignments 明細 (#6) */}
      {perUserEntries.length > 0 && (
        <Card padding="lg" className="mb-4">
          <h2 className="text-base font-bold text-slate-900 mb-3">
            生成済の配分結果 ({perUserEntries.length} 名分)
          </h2>
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="text-xs border-collapse min-w-full">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left p-2 border-r border-b border-slate-200">参加者 UID</th>
                  <th className="text-left p-2 border-r border-b border-slate-200">割当数</th>
                  <th className="text-left p-2 border-b border-slate-200">投稿 ID（先頭5件）</th>
                </tr>
              </thead>
              <tbody>
                {perUserEntries.map(([uid, ids]) => (
                  <tr key={uid}>
                    <td className="p-2 border-r border-b border-slate-200 font-mono" title={uid}>
                      …{uid.slice(-8)}
                    </td>
                    <td className="p-2 border-r border-b border-slate-200">{ids.length} 件</td>
                    <td className="p-2 border-b border-slate-200 font-mono text-slate-500">
                      {ids.slice(0, 5).join(', ')}
                      {ids.length > 5 && ` …他 ${ids.length - 5} 件`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="flex justify-end gap-2 mb-6">
        <button
          type="button"
          onClick={() => navigate(`/districts/${districtId}/verification/assignmentSets`)}
          className="min-h-touch flex items-center px-4 text-sm text-slate-600 hover:text-slate-800"
        >
          キャンセル
        </button>
        <Button onClick={() => void handleSave()} disabled={!canManage || saving}>
          {saving ? '保存中…' : '💾 保存'}
        </Button>
      </div>
    </>
  );
}
